import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { Tooltip } from '@foam/buoy/src/components/Tooltip/Tooltip';
import { FEATURE_FLAGS } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';

import { CheckboxCard } from './CheckboxCard';
import { usePlatformAnalyticsStore } from './usePlatformAnalyticsStore';

const MODASH_ESTIMATED_INFO_COPY =
  'Indicative audience data. Best read directionally.';
const MODASH_ESTIMATED_INFO_TOOLTIP =
  'An estimate for non-connected accounts licensed from a specialist audience measurement provider. Best read directionally.';

interface Props {
  isDemographicsDisabled: boolean;
  isGenderDisabled?: boolean;
  isAgeDisabled?: boolean;
  isCountryDisabled?: boolean;
  isLoading?: boolean;
  showEstimatedInfo?: boolean;
}

const DemographicsSkeleton = () => (
  <div className="flex flex-col gap-2">
    <div className="h-4 w-40 rounded-4 bg-neutral-50" />
    <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex items-center gap-10 rounded-12 border border-neutral-50 bg-card-background-color-default px-6 py-3"
        >
          <div className="size-14 rounded-4 bg-neutral-50" />
          <div className="h-3 w-40 rounded-4 bg-neutral-50" />
        </div>
      ))}
    </div>
  </div>
);

export const PlatformDemographics = ({
  isDemographicsDisabled,
  isGenderDisabled,
  isAgeDisabled,
  isCountryDisabled,
  isLoading,
  showEstimatedInfo = false,
}: Props) => {
  const { selectedDemographics, setSelectedDemographics } =
    usePlatformAnalyticsStore();

  const { isEnabled: isMediaKitsGrowthRateDesignsEnabled } = useFeature(
    FEATURE_FLAGS.foamMediaKitsGrowthRateDesigns,
  );

  const getGenderImage = () =>
    !isDemographicsDisabled && !isGenderDisabled
      ? isMediaKitsGrowthRateDesignsEnabled
        ? '/images/talent-charts/gender_chart.png'
        : '/images/media-kits/donut.png'
      : isMediaKitsGrowthRateDesignsEnabled
        ? '/images/talent-charts/gender_chart_disabled.png'
        : '/images/media-kits/disabled_donut.png';

  const getAgeImage = () =>
    !isDemographicsDisabled && !isAgeDisabled
      ? isMediaKitsGrowthRateDesignsEnabled
        ? '/images/talent-charts/age_chart.png'
        : '/images/media-kits/bar.png'
      : isMediaKitsGrowthRateDesignsEnabled
        ? '/images/talent-charts/age_chart_disabled.png'
        : '/images/media-kits/disabled_bar.png';

  const getCountryImage = () =>
    !isDemographicsDisabled && !isCountryDisabled
      ? isMediaKitsGrowthRateDesignsEnabled
        ? '/images/talent-charts/countries_chart.png'
        : '/images/media-kits/country.png'
      : isMediaKitsGrowthRateDesignsEnabled
        ? '/images/talent-charts/countries_chart_disabled.png'
        : '/images/media-kits/disabled_country.png';

  const getGrowthRateImage = () =>
    !isDemographicsDisabled
      ? '/images/talent-charts/growth_rate_chart.png'
      : '/images/talent-charts/growth_rate_chart_disabled.png';

  if (isLoading) {
    return <DemographicsSkeleton />;
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-16 font-500">Audience Demographics</p>
      {showEstimatedInfo ? (
        <div className="flex items-center gap-1 text-12 font-400 text-neutral-500">
          <span>{MODASH_ESTIMATED_INFO_COPY}</span>
          <Tooltip content={MODASH_ESTIMATED_INFO_TOOLTIP} placement="top">
            <span
              className="inline-flex items-center justify-center text-neutral-500"
              aria-label="Estimated audience data provider info"
            >
              <Icon name="Info" className="text-14" />
            </span>
          </Tooltip>
        </div>
      ) : null}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
        <CheckboxCard
          isChecked={
            !isDemographicsDisabled &&
            !isGenderDisabled &&
            selectedDemographics.includes('gender')
          }
          onChange={() => setSelectedDemographics('gender')}
          isDisabled={isDemographicsDisabled || !!isGenderDisabled}
        >
          <div className="flex items-center gap-4">
            <img src={getGenderImage()} alt="" className="h-12 w-auto" />
            <div className="flex flex-col gap-1">
              <h3 className="text-16 font-500 leading-2">
                Gender distribution
              </h3>
              {(isDemographicsDisabled || isGenderDisabled) && (
                <p className="text-12 font-400 text-root-text-color-subtle">
                  Chart data is unavailable
                </p>
              )}
            </div>
          </div>
        </CheckboxCard>
        <CheckboxCard
          isChecked={
            !isDemographicsDisabled &&
            !isAgeDisabled &&
            selectedDemographics.includes('age')
          }
          onChange={() => setSelectedDemographics('age')}
          isDisabled={isDemographicsDisabled || !!isAgeDisabled}
        >
          <div className="flex items-center gap-4">
            <img src={getAgeImage()} alt="" className="h-12 w-auto" />
            <div className="flex flex-col gap-1">
              <h3 className="text-16 font-500 leading-2">Age distribution</h3>
              {(isDemographicsDisabled || isAgeDisabled) && (
                <p className="text-12 font-400 text-root-text-color-subtle">
                  Chart data is unavailable
                </p>
              )}
            </div>
          </div>
        </CheckboxCard>
        <CheckboxCard
          isChecked={
            !isDemographicsDisabled &&
            !isCountryDisabled &&
            selectedDemographics.includes('country')
          }
          onChange={() => setSelectedDemographics('country')}
          isDisabled={isDemographicsDisabled || !!isCountryDisabled}
        >
          <div className="flex items-center gap-4">
            <img src={getCountryImage()} alt="" className="h-12 w-auto" />
            <div className="flex flex-col gap-1">
              <h3 className="text-16 font-500 leading-2">
                Country distribution
              </h3>
              {(isDemographicsDisabled || isCountryDisabled) && (
                <p className="text-12 font-400 text-root-text-color-subtle">
                  Chart data is unavailable
                </p>
              )}
            </div>
          </div>
        </CheckboxCard>
        {isMediaKitsGrowthRateDesignsEnabled ? (
          <CheckboxCard
            isChecked={
              !isDemographicsDisabled &&
              selectedDemographics.includes('growthRate')
            }
            onChange={() => setSelectedDemographics('growthRate')}
            isDisabled={isDemographicsDisabled}
          >
            <div className="flex items-center gap-4">
              <img src={getGrowthRateImage()} alt="" className="h-12 w-auto" />
              <h3 className="text-16 font-500 leading-2">Follower growth</h3>
            </div>
          </CheckboxCard>
        ) : null}
      </div>
    </div>
  );
};
